import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AppLayout } from "../shared/layout";
import { calculatePayment } from "@/data/requests/payment-requests";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import {
  Calculator,
  Upload,
  FileText,
  Download,
  CheckCircle,
  XCircle,
  Users,
} from "lucide-react";

export function PaymentCalculateScreen() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);

  const pageSettings = {
    title: "Ödeme Hesapla",
    actions: [
      {
        name: "Listeye Dön",
        color: "blue",
        icon: "list-icon",
        onClick: () => {
          navigate("/payments");
        },
      },
    ],
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const mutation = useMutation({
    mutationKey: ["calculatePayment"],
    mutationFn: (data) => {
      return calculatePayment(data);
    },
    onSuccess: (response) => {
      setResult(response.data);
      toast({
        title: "Başarılı",
        description: "Excel işlendi. Sonuçları indirebilirsiniz.",
      });
    },
    onError: (error) => {
      console.log("error", error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: error.response?.data?.message || "Bir hata oluştu",
      });
    },
  });

  const onSubmit = async (data) => {
    const formData = new FormData();
    formData.append("file", data.excelFile[0]);
    mutation.mutate(formData);
  };

  const downloadExcel = (base64Data, filename) => {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (mutation.isPending) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <Calculator className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
            <p className="text-lg font-medium">Excel işleniyor...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={pageSettings.title} actions={pageSettings.actions}>
      <div className="px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Upload Card */}
          <Card>
            <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Upload className="h-5 w-5 text-green-600" />
                Excel Yükle
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <Label htmlFor="excelFile" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Excel Dosyası *
                  </Label>
                  <Input
                    id="excelFile"
                    name="excelFile"
                    type="file"
                    accept=".xlsx,.xls"
                    className="mt-1.5"
                    {...register("excelFile", {
                      required: "Excel dosyası gerekli",
                    })}
                  />
                  {errors.excelFile && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.excelFile.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Excel dosyasında <strong>influencerName</strong> ve{" "}
                    <strong>amount</strong> sütunları olmalıdır.
                  </p>
                </div>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                  >
                    <Calculator className="mr-2 h-4 w-4" />
                    Hesapla
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Results Card */}
          {result && (
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  Sonuçlar
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-muted rounded-lg p-4 text-center">
                    <Users className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-2xl font-bold">{result.totalRecords}</p>
                    <p className="text-sm text-muted-foreground">Toplam Kayıt</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4 text-center">
                    <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-600">
                      {result.clientsFound}
                    </p>
                    <p className="text-sm text-muted-foreground">Müşteri Bulundu</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-4 text-center">
                    <XCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-600">
                      {result.clientsNotFound}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Client Bulunamadı
                    </p>
                  </div>
                </div>

                {/* Download Button */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium mb-4">Excel İndir</h3>
                  <div className="flex justify-center">
                    <Button
                      onClick={() =>
                        downloadExcel(result.resultExcel, "payment-hesaplama.xlsx")
                      }
                      variant="outline"
                      className="h-auto py-4 px-8 flex flex-col items-center gap-2"
                    >
                      <Download className="h-6 w-6 text-green-600" />
                      <span className="font-medium">Hesaplama Excel</span>
                      <span className="text-xs text-muted-foreground">
                        IG Name, Ajans, TY Kazanç, NewTV Kar, INF Kazanç, Ajans kar, Final Kazanç
                      </span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
